import {
  background,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { organizationRepository } from "api/organizationRepository";
import { useEffect, useState } from "react";
import { departmentSchema, flattenZodErrors } from "validations/Validations";
import { z } from "zod";
import CreatableSelect from "react-select/creatable";
import { useAuth } from "contexts/authContext";


interface DepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  branches: any[];
  onDepartmentAdded: (updatedBranches: any[]) => void;
  showCenteredAlert: (
    msg: string,
    status?: "success" | "info" | "warning" | "error"
  ) => void;
}

export default function DepartmentModal({
  isOpen,
  onClose,
  organizationId,
  branches,
  onDepartmentAdded,
  showCenteredAlert,
}: DepartmentModalProps) {
  const [selectedLocation, setSelectedLocation] = useState<any | null>(null);
  const [departmentName, setDepartmentName] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAdding, setIsAdding] = useState(false);
  const cardBg = useColorModeValue('white', 'navy.800');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const { role, user } = useAuth();
  const handleAddDepartment = async () => {
    const payload = {
      departmentName,
      locationId: selectedLocation?.locationId || '',
      locationName: selectedLocation?.name || '',
    };

    try {
      departmentSchema.parse(payload);
      setErrors({});
      setIsAdding(true);

      await organizationRepository.addDepartment(
        organizationId,
        payload.locationId,
        payload
      );

      const updatedOrg = await organizationRepository.getOrganizationById(
        organizationId
      );
      onDepartmentAdded(updatedOrg?.locations || []);
      showCenteredAlert("Department added successfully", "success");

      setDepartmentName("");
      setSelectedLocation(null);
      onClose();
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(flattenZodErrors(err));
        showCenteredAlert("Please fix the validation errors.", "warning");
      } else {
        showCenteredAlert("Failed to add department", "error");
      }
    } finally {
      setIsAdding(false);
    }
  };
  useEffect(() => {
    if (role === "LocationAdmin" && user?.locationId) {
      const location = branches.find(
        (b) => b.locationId === user.locationId
      );

      if (location) {
        setSelectedLocation(location);
      }
    }
  }, [role, user?.locationId, branches]);
  useEffect(() => {
    setDepartmentName("");
  }, [])

  // select depatment
  const allDepartments = ["Melting", "Moulding", "Production", "Maintenance", "Lab"];

  const availableDepartments = allDepartments.filter(
    (dept) =>
      !selectedLocation?.departments?.some(
        (d: any) => d.departmentName.toLowerCase() === dept.toLowerCase()
      )
  );

  const options = availableDepartments.map((dept) => ({
    label: dept,
    value: dept,
  }));
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={cardBg}>
        <ModalHeader>Add New Department</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Plant</FormLabel>
              <Select
                placeholder="Select Plant"
                value={selectedLocation?.locationId || ""}
                onChange={(e) => {
                  const branch = branches.find(
                    (b: any) => b.locationId === e.target.value
                  );
                  setSelectedLocation(branch);
                }}
                bg={inputBg}
                color={selectedLocation ? textColor : placeholderColor}
                isDisabled={role === "LocationAdmin" && user?.locationId ? true : false}
              >
                {branches.map((branch: any) => (
                  <option
                    key={branch.locationId}
                    value={branch.locationId}
                    style={{
                      backgroundColor: menuListBg,
                      color: textColor,
                    }}
                  >
                    {branch.name}
                  </option>
                ))}
              </Select>
              {errors.locationId && (
                <Text fontSize="xs" color="red.500">{errors.locationId}</Text>
              )}
            </FormControl>

            <FormControl>
              <FormLabel>Department Name</FormLabel>

              <Select
                placeholder="Select Department"
                value={departmentName}
                onChange={(e) => setDepartmentName(e.target.value)}
                bg={inputBg}
                color={departmentName ? textColor : placeholderColor}
              >
                {options.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    style={{
                      backgroundColor: menuListBg,
                      color: textColor,
                    }}
                  >
                    {opt.label}
                  </option>
                ))}
              </Select>

              {errors.departmentName && (
                <Text fontSize="xs" color="red.500">
                  {errors.departmentName}
                </Text>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3}>Cancel</Button>
          <Button
            onClick={handleAddDepartment}
            colorScheme="blue"
            isLoading={isAdding}
          >
            Add
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
